import { cn } from '@crowdex/ui/lib/utils';
import { ChevronDown } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { readDevicePreference, writeDevicePreference } from '@/lib/device-preferences';

/** Rows a section shows before "See all · N" (brand: "Réglages et résumé : 3"). */
export const SUMMARY_ROWS = 3;

type SectionMemory = { collapsed: boolean; expanded: boolean };

const memoryKey = (title: string) => `crowdex.summary.section.${title}`;

/** How the user left a section: folded by its chevron, unfolded past its first rows. */
export function readSectionMemory(title: string): SectionMemory {
  try {
    const saved = JSON.parse(readDevicePreference(memoryKey(title)) ?? '{}') as Partial<
      Record<keyof SectionMemory, unknown>
    >;
    return { collapsed: saved.collapsed === true, expanded: saved.expanded === true };
  } catch {
    return { collapsed: false, expanded: false };
  }
}

/** The section's memory, kept per section for the user (device preferences). */
function useSectionMemory(title: string) {
  const [memory, setMemory] = useState<SectionMemory>(() => readSectionMemory(title));
  const update = (change: Partial<SectionMemory>) => {
    const next = { ...memory, ...change };
    setMemory(next);
    try {
      void writeDevicePreference(memoryKey(title), JSON.stringify(next)).catch(() => undefined);
    } catch {
      // No storage: the section keeps its state while shown.
    }
  };
  return [memory, update] as const;
}

/**
 * A section of the Summary card (D5, mobile 36): its name in the muted ink, how many it holds
 * beside it ("Artifacts 2"), then its rows. Its chevron folds it; given `rows`, it shows the
 * first three and "See all · N" beside its title unfolds the rest in place (one more row than
 * that is not hidden). Both are remembered per section.
 */
export function SummarySection({
  children,
  count,
  title,
  rows,
  card = false,
}: {
  children?: ReactNode;
  count?: number;
  title: string;
  /** Its rows, of which the first three show until "See all · N". */
  rows?: readonly ReactNode[];
  /** On a phone its rows sit on a card of their own (mobile 36). */
  card?: boolean;
}) {
  const [memory, remember] = useSectionMemory(title);
  const foldable = rows !== undefined && rows.length > SUMMARY_ROWS + 1;
  const shownRows =
    rows === undefined ? null : foldable && !memory.expanded ? rows.slice(0, SUMMARY_ROWS) : rows;
  const content = (
    <>
      {shownRows}
      {children}
    </>
  );
  return (
    <section aria-label={title} className="grid gap-1" data-summary-section={title}>
      <h3 className="type-meta flex items-center gap-1.5 font-medium text-muted-foreground">
        <button
          aria-expanded={!memory.collapsed}
          aria-label={`${memory.collapsed ? 'Show' : 'Hide'} ${title}`}
          className="-ml-1 inline-flex min-w-0 items-center gap-1.5 rounded px-1 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-summary-chevron
          onClick={() => remember({ collapsed: !memory.collapsed })}
          type="button"
        >
          <span>{title}</span>
          {count !== undefined && (
            <span className="font-normal text-muted-foreground/80 tabular-nums">{count}</span>
          )}
          <ChevronDown
            aria-hidden
            className={cn(
              'size-3.5 shrink-0 transition-transform',
              memory.collapsed && '-rotate-90',
            )}
          />
        </button>
        {foldable && !memory.collapsed && (
          <button
            aria-expanded={memory.expanded}
            className="ml-auto shrink-0 font-normal transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-summary-see-all
            onClick={() => remember({ expanded: !memory.expanded })}
            type="button"
          >
            {memory.expanded ? 'Show less' : `See all · ${rows.length}`}
          </button>
        )}
      </h3>
      {memory.collapsed ? null : card ? (
        <div className="grid gap-1 pointer-coarse:rounded-2xl pointer-coarse:border pointer-coarse:bg-surface-raised pointer-coarse:px-3.5 pointer-coarse:py-2">
          {content}
        </div>
      ) : (
        content
      )}
    </section>
  );
}

/** One line of a section: what it is on the left, its value on the right ("Branch  main"). */
export function SummaryRow({
  label,
  children,
  className,
}: {
  label: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('type-ui flex min-w-0 items-center gap-3 py-0.5', className)}>
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="ml-auto min-w-0 truncate text-right text-foreground">{children}</span>
    </div>
  );
}

/** A row that opens something: an icon, its name, and what it says on the right. */
export function SummaryButtonRow({
  icon,
  children,
  trailing,
  onClick,
  title,
}: {
  icon: ReactNode;
  children: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  title?: string;
}) {
  const content = (
    <>
      <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground [&_svg]:size-4">
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate text-left text-foreground">{children}</span>
      {trailing !== undefined && (
        <span className="shrink-0 text-muted-foreground tabular-nums">{trailing}</span>
      )}
    </>
  );
  const className =
    '-mx-2 type-ui flex min-w-0 items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors';
  if (onClick === undefined)
    return (
      <div className={className} data-summary-row title={title}>
        {content}
      </div>
    );
  return (
    <button
      className={cn(
        className,
        'hover:bg-accent/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
      data-summary-row
      onClick={onClick}
      title={title}
      type="button"
    >
      {content}
    </button>
  );
}

/** The actions a section offers, under its rows: outline pills, as many as apply. */
export function SummaryActions({ children, label }: { children: ReactNode; label: string }) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of buttons, not a form's fieldset.
    <div aria-label={label} className="flex flex-wrap gap-1.5 pt-1.5 pb-0.5" role="group">
      {children}
    </div>
  );
}

/** One of a section's actions: an outline pill (the screen's one filled button is elsewhere). */
export function SummaryPill({
  children,
  icon,
  onClick,
  disabled = false,
  title,
}: {
  children: ReactNode;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      className="type-meta inline-flex h-7 min-w-0 max-w-full shrink-0 items-center gap-1.5 rounded-control border border-hairline-strong px-3 text-foreground transition-colors hover:bg-accent/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:text-muted-foreground"
      disabled={disabled}
      onClick={onClick}
      title={title}
      type="button"
    >
      {icon}
      <span className="truncate">{children}</span>
    </button>
  );
}
